import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import useAuth from '../hooks/useAuth';
import useActiveCondominio from '../hooks/useActiveCondominio';
import axios from 'axios';

export const FeaturesContext = createContext();

/**
 * FeaturesProvider - Context para gerenciar features globalmente
 * 
 * Carrega features uma única vez ao logar e ao mudar condomínio
 * Disponibiliza para todos os componentes via useFeatures hook
 * 
 * Fornece:
 * - features: array
 * - loading: boolean
 * - error: string | null
 * - refetch: () => Promise
 */
export const FeaturesProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { activeCondominio, condominios } = useActiveCondominio();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Carrega features do servidor
   */
  const loadFeatures = useCallback(async () => {
    const allowedIds = (condominios || []).map((c) => String(c.id));

    if (!activeCondominio || !user) {
      setLoading(false);
      setFeatures([]);
      return;
    }

    if (allowedIds.length > 0 && !allowedIds.includes(String(activeCondominio))) {
      setLoading(false);
      setFeatures([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      // Admin carrega todas as features, outros carregam apenas as permitidas
      const endpoint = user.cargo === 'admin'
        ? `${apiUrl}/api/features?scope=todas`
        : `${apiUrl}/api/features`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-condominio-id': activeCondominio
        }
      });

      const featuresData = response.data?.dados || response.data?.data || [];
      setFeatures(featuresData);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar features');
      console.error('[FeaturesContext] Erro:', err);
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, [activeCondominio, user, condominios]);

  /**
   * Carrega features quando activeCondominio muda ou user loga
   */
  useEffect(() => {
    const allowedIds = (condominios || []).map((c) => String(c.id));
    if (!authLoading && activeCondominio && user && allowedIds.includes(String(activeCondominio))) {
      loadFeatures();
    }
  }, [activeCondominio, authLoading, user, condominios, loadFeatures]);

  const value = useMemo(() => ({
    features,
    loading,
    error,
    refetch: loadFeatures
  }), [features, loading, error, loadFeatures]);

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
};

export default FeaturesContext;
